import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { ReduxSelect } from 'components/FormElements/ReduxForm'
// constants
import { Forms } from 'constants/index'
// views
import { CloseIcon } from 'components/Modals/SystemProcessModal/Form/components/views'
// types
import { SystemProcessConditionAction } from 'types/graphql'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { Wrapper, ValueFieldWrapper } from './views'
// store
import { responseTypesSelector } from 'store/reducers/riskTemplates'
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// utils
import { required } from 'utils/index'
import { getFieldsList, getReviewFieldIds } from 'utils/fields'
import {
  getValueField,
  getRTValueField,
  getActionOptions,
  getRTActionOptions,
  getEntityOrFieldOptions,
  getRTEntityOrFieldOptions
} from 'utils/systemProcess'

interface IIncomingProps {
  field: any
  removeField: () => void
  groupIndex: number
  index: number
}

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

export const SubCondition: React.FC<IIncomingProps> = ({ field, removeField, groupIndex, index }) => {
  const dispatch = useDispatch()

  const { isRiskTemplate, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const objectEntities = useSelector(objectEntitiesSelector)
  const responseTypeSets = useSelector(responseTypesSelector)

  const { action, object, entityOrField, processType } = useSelector((state) => ({
    action: selector(state, `${field}.action.value`),
    object: selector(state, 'object'),
    entityOrField: selector(state, `${field}.entityOrField`),
    processType: selector(state, 'processType.value')
  }))

  const isFieldRequired = action !== SystemProcessConditionAction.HasChanged

  const fields = React.useMemo(() => {
    if (object) {
      return getFieldsList(objectEntities, object.label)
    }
  }, [object, objectEntities])

  const reviewFieldIds = React.useMemo(
    () => (object ? getReviewFieldIds(objectEntities, object.label) : []),
    [object, objectEntities]
  )

  const onEntityOrFieldSelectChange = () => {
    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.action`, null))
    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.value`, null))
  }

  const onActionSelectChange = () => {
    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, `${field}.value`, null))
  }

  return (
    <Wrapper
      data-testid='system-process-condition'
      data-entity-id={`${groupIndex}.${index}`}>
      Where
      <ReduxSelect
        validate={[required]}
        name={`${field}.entityOrField`}
        onSelectChange={onEntityOrFieldSelectChange}
        options={
          isRiskTemplate
            ? getRTEntityOrFieldOptions({ riskTemplate, responseTypeSets })
            : getEntityOrFieldOptions(fields, object, processType, reviewFieldIds)
        }
      />
      <ReduxSelect
        validate={[required]}
        name={`${field}.action`}
        onSelectChange={onActionSelectChange}
        options={
          isRiskTemplate
            ? getRTActionOptions(entityOrField, processType)
            : getActionOptions(entityOrField, object, processType)
        }
      />
      <ValueFieldWrapper>
        {isRiskTemplate
          ? getRTValueField({ entityOrField, action, field, isFieldRequired, objectId: riskTemplate.id })
          : getValueField({ action, field, entityOrField, object, isFieldRequired })}
      </ValueFieldWrapper>
      <CloseIcon
        data-testid='system-process-remove-condition'
        data-entity-id={`${groupIndex}.${index}`}
        onClick={removeField}
        marginLeft={8}
      />
    </Wrapper>
  )
}

export default SubCondition
